import { Link } from "react-router-dom";
import { Inbox, UserX, BookOpen } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { formatNoticeDateList } from "@/lib/timezone";
import { CATEGORY_LABELS, noticeDates, noticeRequestedStatus } from "@/lib/types";
import type { DashboardActionInbox } from "@/lib/types";
import { MessageSquareWarning } from "lucide-react";

interface ActionInboxCardProps {
  inbox: DashboardActionInbox;
}

export function ActionInboxCard({ inbox }: ActionInboxCardProps) {
  const pendingMantras = inbox.pending_mantras_count ?? 0;
  const hasItems =
    inbox.pending_notices_count > 0 ||
    inbox.no_punch_count > 0 ||
    pendingMantras > 0;

  if (!hasItems) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/50">
        <CardContent className="py-4 text-sm text-emerald-800">
          Action inbox clear — no pending notices and everyone expected has
          punched today.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-saffron-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Action inbox</CardTitle>
        <p className="text-sm text-muted-foreground">
          Items needing coordinator attention today
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {inbox.pending_notices_count > 0 && (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Inbox className="h-4 w-4 text-saffron-700" />
                Pending notices
                <Badge>{inbox.pending_notices_count}</Badge>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/notices">Review all</Link>
              </Button>
            </div>
            <ul className="divide-y rounded-md border text-sm">
              {inbox.pending_notices.map((n) => (
                <li
                  key={n.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <div>
                    <p className="font-medium">{n.person_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatNoticeDateList(noticeDates(n))}
                      {noticeDates(n).length > 1
                        ? ` · ${noticeDates(n).length} days`
                        : ""}{" "}
                      · {noticeRequestedStatus(n)}
                      {n.reason ? ` · ${n.reason}` : ""}
                    </p>
                  </div>
                  <Badge variant="notice">Awaiting decision</Badge>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pendingMantras > 0 && (
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <BookOpen className="h-4 w-4 text-saffron-700" />
                Yoga mahamantras to confirm
                <Badge>{pendingMantras}</Badge>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/mantras">Review</Link>
              </Button>
            </div>
          </section>
        )}

        {inbox.no_punch_count > 0 && (
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <UserX className="h-4 w-4 text-red-700" />
              No punch today
              <Badge variant="secondary">{inbox.no_punch_count}</Badge>
            </div>
            <ul className="divide-y rounded-md border text-sm">
              {inbox.no_punch_today.map((p) => (
                <li
                  key={`${p.person_id}-${p.category}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <div>
                    <p className="font-medium">{p.person_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {CATEGORY_LABELS[p.category]} ·{" "}
                      {p.session.replace(/_/g, " ")}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1">
                    <AttendanceCellBadge
                      cell={{ status: p.status }}
                    />
                    {p.has_notice && (
                      <MessageSquareWarning
                        className="h-3.5 w-3.5 text-saffron-600"
                        aria-label="Notice submitted"
                      />
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
